import { MetricCard } from '@/features/reports/components/MetricCard';
import { ReportCard, ReportRow } from '@/features/reports/components/ReportCard';
import {
  useCoversTurnover,
  useDailySales,
  usePeakHours,
} from '@/features/reports/hooks/useReports';
import type { DateRange } from '@/features/reports/lib/range';
import { useT } from '@/i18n';
import { formatMoney } from '@/utils/money';
import { DayPartBars, HourBars } from './SalesSection';

const TOP_TABLES_PREVIEW = 6;

/** How busy the room was: covers, table turns, and when the rush lands.
 *  Dine-in only - the caller renders it behind the table-ordering flag. */
export function OperationsSection({ range }: { range: DateRange }) {
  const t = useT();
  const ov = t.reports.overview;
  const so = t.reports.salesOverview;

  const { data: covers } = useCoversTurnover(range);
  const { data: peak } = usePeakHours(range);
  const { data: sales } = useDailySales(range);
  const cur = covers?.currency ?? sales?.currency ?? 'USD';

  return (
    <>
      <div className="rpt-kpis">
        <MetricCard label={so.covers} value={String(covers?.dineInCovers ?? 0)} />
        <MetricCard label={so.avgTab} value={formatMoney(covers?.avgTabCents ?? 0, cur)} />
        <MetricCard
          label={ov.tablesUsed}
          value={String(covers?.distinctTables ?? 0)}
          caption={ov.turnsPerTable((covers?.sessionsPerTable ?? 0).toFixed(1))}
        />
        <MetricCard
          label={ov.avgDwell}
          value={ov.minutes(Math.round(covers?.avgDwellMinutes ?? 0))}
        />
      </div>

      <div className="rpt-row">
        {peak && peak.hours.length > 0 && (
          <ReportCard className="rpt-main" title={ov.byHour}>
            <HourBars hours={peak.hours} cur={cur} />
          </ReportCard>
        )}
        {peak && peak.dayParts.length > 0 && (
          <ReportCard title={so.byTimeOfDay}>
            <DayPartBars parts={peak.dayParts} cur={cur} />
          </ReportCard>
        )}
      </div>

      {covers && covers.turnsPerTable.length > 0 && (
        <div className="rpt-row">
          <ReportCard className="rpt-wide" title={ov.busiestTables}>
            {covers.turnsPerTable.slice(0, TOP_TABLES_PREVIEW).map((tt) => (
              <ReportRow key={tt.tableLabel} label={tt.tableLabel} value={ov.turns(tt.turns)} />
            ))}
          </ReportCard>
        </div>
      )}
    </>
  );
}
